import { SpanStatusCode } from '@opentelemetry/api';
import prom from 'promjs-plus';
import { z } from 'zod';
import { endpoints } from '@rossum/api-client';
import { isFeatureEnabled } from '../../features/feature-enablement/featureEnablement';
import { api } from '../../lib/apiClient';
import { getNetworkQuality } from '../../metrics/measureLatency';
import { tracer } from '../../metrics/openTelemetry';
import { sendMetricsToPrometheus } from '../../metrics/sendMetricsToPrometheus';
import { isFeatureFlagEnabled } from '../../unleash/useFeatureFlag';
import { timers } from '../timers';
import { calculateIntervals } from '../utils';

// TODO: Handle status 429?

export const documentReadyMetricName = 'fe_document_ready_after_action';

const registry = prom();

const log = (message: string, ...params: unknown[]) => {
  if (isFeatureEnabled('log-metrics-to-prometheus', {}))
    // oxlint-disable-next-line no-console
    console.log(`⏱️ [PROM] ${message}`, ...params);
};

const metadataSchema = z.object({
  action: z.enum([
    'annotation_confirm',
    'annotation_next',
    'annotation_postpone',
    'annotation_open',
  ]),
});

type Action = z.infer<typeof metadataSchema>['action'];

type SendMetricToPrometheus = {
  action: Action;
  timeInMs: number;
  organization: string;
};

type StopAnnotationValidateAndSendResults = {
  organization: string;
  annotation: string;
  queue: string;
  logMetrics: boolean;
};

const createHistogramAndSaveMetrics = ({
  action,
  timeInMs,
  organization,
}: SendMetricToPrometheus) => {
  const buckets = [1, 2, 3, 5, 8, 10, 11, 13, 15, 18, 21];

  const isOrganizationInHypercare =
    isFeatureFlagEnabled('hypercare-metrics') && organization;

  const histogram = registry.create(
    'histogram',
    documentReadyMetricName,
    'Time to load document after action next, postpone or confirm',
    buckets
  );

  const timeInSeconds = timeInMs / 1000;

  const networkQuality = getNetworkQuality();

  histogram.observe(timeInSeconds, {
    action,
    ...(isOrganizationInHypercare
      ? { organizationUrl: organization }
      : undefined),
    ...(networkQuality !== 'unknown' ? { networkQuality } : undefined),
  });

  const results = registry.metrics();

  sendMetricsToPrometheus(results);
  log(`results: ${results}`, 'font-weight:bold');

  histogram.resetAll();
  registry.clear();
};

type SendMetricsToOTEL = Omit<SendMetricToPrometheus, 'timeInMs'> & {
  startTimeInMs: number;
};

const sendMetricsToOpenTelemetry = ({
  startTimeInMs,
  organization,
  action,
}: SendMetricsToOTEL) => {
  const isOpenTelemetryEnabled = isFeatureEnabled('open-telemetry', {});

  if (!isOpenTelemetryEnabled) {
    return;
  }

  const span = tracer.startSpan('document_loaded', {
    attributes: {
      'r8.sli.v1.event': 'document_loaded',
      'r8.ui.action': action.replace('annotation', 'document'),
      ...(organization ? { 'r8.elis.organization.url': organization } : {}),
    },
    startTime: startTimeInMs,
  });

  // Set span status as OK since we're measuring successful completion
  span.setStatus({ code: SpanStatusCode.OK });

  // End the span immediately to send it
  span.end();
};

const metrics = {
  startAnnotationConfirm: () => {
    timers.start(documentReadyMetricName, {
      action: 'annotation_confirm',
    });
  },

  startAnnotationNext: () => {
    timers.start(documentReadyMetricName, {
      action: 'annotation_next',
    });
  },

  startAnnotationOpen: (annotationId: number) => {
    timers.start(documentReadyMetricName, {
      action: 'annotation_open',
      annotationId,
    });
  },

  startAnnotationPostpone: () => {
    timers.start(documentReadyMetricName, {
      action: 'annotation_postpone',
    });
  },

  stopAnnotationValidate: () => {
    timers.stop(documentReadyMetricName);
  },

  stopAnnotationValidateAndSendResults: ({
    organization,
    annotation,
    queue,
    logMetrics,
  }: StopAnnotationValidateAndSendResults) => {
    const stop = timers.stop(documentReadyMetricName);
    const interval = stop?.interval;
    const metadata = stop?.metadata;

    if (!interval || !metadata) return;

    const parsed = metadataSchema.safeParse(metadata);

    if (!parsed.success) {
      // oxlint-disable-next-line no-console
      console.warn(parsed.error);
      return;
    }

    const { overall } = calculateIntervals({
      interval,
      // TODO: What should be in metadata?
      metadata: [],
    });

    const { action } = parsed.data;

    const logsPayload = {
      duration: overall / 1000,
      type: documentReadyMetricName,
      subtype: action,
      annotation,
      queue,
    };

    if (logMetrics) {
      try {
        api.request(endpoints.internal.saveLogs(logsPayload));
      } catch (error) {
        if (window.Sentry) {
          window.Sentry.captureException(error);
        }
      }
    }

    createHistogramAndSaveMetrics({
      action,
      timeInMs: overall,
      organization,
    });

    sendMetricsToOpenTelemetry({
      startTimeInMs: interval[0],
      organization,
      action,
    });
  },
};

export { metrics };
